// Saint-Aubry et ses environs : petite ville de province tombée la première, sa zone industrielle, un camp militaire
// de quarantaine, une ferme, le bois des Combes, le hameau du Moulin et, au sud, la base militaire de Ravel.
// Carte faite à la main (300 × 300 m).
// Repère : x vers l'est, y vers le sud, en mètres. Les murs bloquent la vue et les balles ;
// le couvert bas (voitures, caisses, comptoirs, barrières, grillages) bloque les balles et le passage, pas la vue.
import { footprint, modelHeight } from './props';

export type RectKind = 'wall' | 'low';

export interface Rect {
  x0: number; y0: number; x1: number; y1: number;
  kind: RectKind;
  height: number; // m, pour le rendu
  look?: string; // apparence : 'model' (un modèle 3D le représente), 'shelf', 'counter', 'barrier', 'fountain', 'chainlink'…
  bid?: number; // bâtiment auquel appartient un mur
  ext?: boolean; // mur extérieur (on y pose des fenêtres)
  door?: string; // porte verrouillée : identifiant de la serrure (ouverte avec la bonne clé, pour tout le raid)
}

export interface Zone { name: string; x0: number; y0: number; x1: number; y1: number; indoor: boolean }
export interface Container { id: number; x: number; y: number; kind: string; model: string; rot: number }
// cost : crédits prélevés sur le profil à la sortie (extraction payante)
export interface Extract { name: string; x: number; y: number; r: number; cost?: number }
// Serrure : nom affiché, clé qui l'ouvre, point où se tenir pour l'ouvrir
export interface Lock { id: string; name: string; key: string; x: number; y: number }
export type FloorMat = 'asphalt' | 'sidewalk' | 'dirt' | 'concrete' | 'wood' | 'tiles' | 'parking' | 'gravel';
export interface Floor { x0: number; y0: number; x1: number; y1: number; mat: FloorMat }
export interface Building {
  id: number; name: string; x0: number; y0: number; x1: number; y1: number; wall: number; roof: number;
  style?: 'plaster' | 'render' | 'brick' | 'painted' | 'metal' | 'wood'; // matériau des façades
  roofKind?: 'clay' | 'slate' | 'metal' | 'flat';
}
export interface Prop { model: string; x: number; y: number; rot: number; free?: number } // free : angle libre (radians) au lieu des quarts de tour
export interface Line { x0: number; y0: number; x1: number; y1: number; w: number; rail?: boolean; furrow?: boolean }
export interface Lintel { x0: number; y0: number; x1: number; y1: number; bid: number }

export interface GameMap {
  name: string;
  width: number;
  height: number;
  rects: Rect[];
  zones: Zone[];
  spawns: [number, number][];
  containers: Container[];
  extracts: Extract[];
  floors: Floor[];
  buildings: Building[];
  props: Prop[];
  lines: Line[];
  lintels: Lintel[];
  locks: Lock[];
  open?: Set<string>; // serrures ouvertes dans ce raid (copie de la carte propre à chaque partie)
}

const T = 0.3; // épaisseur des murs
const WALL_H = 3;

type Side = 'n' | 's' | 'e' | 'w';
interface Door { side: Side; at: number; width?: number }

// Hasard reproductible (même carte sur le serveur et le client)
function rng(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildMap(): GameMap {
  const W = 300, H = 300;
  const rects: Rect[] = [], zones: Zone[] = [], containers: Container[] = [], floors: Floor[] = [];
  const buildings: Building[] = [], props: Prop[] = [], lines: Line[] = [], lintels: Lintel[] = [], locks: Lock[] = [];
  const r = rng(1312);

  const wall = (x0: number, y0: number, x1: number, y1: number, extra: Partial<Rect> = {}): Rect => {
    const R: Rect = { x0: Math.min(x0, x1), y0: Math.min(y0, y1), x1: Math.max(x0, x1), y1: Math.max(y0, y1), kind: 'wall', height: WALL_H, ...extra };
    rects.push(R);
    return R;
  };
  const low = (x: number, y: number, w: number, h: number, height: number, look: string) => {
    rects.push({ x0: x, y0: y, x1: x + w, y1: y + h, kind: 'low', height, look });
  };
  const floor = (x0: number, y0: number, x1: number, y1: number, mat: FloorMat) => floors.push({ x0, y0, x1, y1, mat });

  // Objet 3D posé en (x, y) = centre ; solide : bloque passage et balles (couvert bas) ou aussi la vue (mur)
  const prop = (model: string, x: number, y: number, rot = 0, solid: false | 'low' | 'wall' = false) => {
    props.push({ model, x, y, rot });
    if (!solid) return;
    const [fw, fd] = footprint(model, rot);
    rects.push({ x0: x - fw / 2, y0: y - fd / 2, x1: x + fw / 2, y1: y + fd / 2, kind: solid, height: modelHeight(model), look: 'model' });
  };
  // Meuble à fouiller
  const CONT_MODEL: Record<string, string> = {
    caisse: 'crate_wood', armoire: 'cabinet', bureau: 'desk_metal', casier: 'drawers', medical: 'shelves', outils: 'tool_chest', militaire: 'crate_mil', coffre: 'Chest',
  };
  const cont = (x: number, y: number, kind: string, rot = 0) => {
    const model = CONT_MODEL[kind]!;
    prop(model, x, y, rot, 'low');
    containers.push({ id: containers.length, x, y, kind, model, rot });
  };

  // Segment de mur horizontal (y fixe) ou vertical (x fixe), percé d'ouvertures [début, fin)
  function segment(horizontal: boolean, fixed: number, from: number, to: number, gaps: [number, number][], extra: Partial<Rect>) {
    let cur = from;
    for (const [a, b] of [...gaps].sort((p, q) => p[0] - q[0])) {
      if (a > cur) horizontal ? wall(cur, fixed - T / 2, a, fixed + T / 2, extra) : wall(fixed - T / 2, cur, fixed + T / 2, a, extra);
      cur = Math.max(cur, b);
    }
    if (cur < to) horizontal ? wall(cur, fixed - T / 2, to, fixed + T / 2, extra) : wall(fixed - T / 2, cur, fixed + T / 2, to, extra);
  }
  // Grillage : on voit à travers, on ne passe pas ; ouvertures comme pour les murs
  const CHAIN: Partial<Rect> = { kind: 'low', height: 2.4, look: 'chainlink' };
  const fence = (horizontal: boolean, fixed: number, from: number, to: number, gaps: [number, number][] = []) => segment(horizontal, fixed, from, to, gaps, CHAIN);
  // Enclos grillagé (x0, y0)–(x1, y1) avec portails
  const enclosure = (x0: number, y0: number, x1: number, y1: number, gates: Partial<Record<Side, [number, number][]>>) => {
    fence(true, y0, x0, x1, gates.n); fence(true, y1, x0, x1, gates.s);
    fence(false, x0, y0, y1, gates.w); fence(false, x1, y0, y1, gates.e);
  };

  // Bâtiment : murs extérieurs percés de portes, sol, toit ; `at` = distance depuis le coin nord-ouest le long du mur
  function building(name: string, x: number, y: number, w: number, h: number, doors: Door[], wallColor: number, roof: number, mat: FloorMat = 'wood', look: Pick<Building, 'style' | 'roofKind'> = {}) {
    const bid = buildings.length;
    buildings.push({ id: bid, name, x0: x, y0: y, x1: x + w, y1: y + h, wall: wallColor, roof, ...look });
    zones.push({ name, x0: x, y0: y, x1: x + w, y1: y + h, indoor: true });
    floor(x, y, x + w, y + h, mat);
    const gaps = (side: Side, base: number): [number, number][] => doors.filter(d => d.side === side).map(d => [base + d.at, base + d.at + (d.width ?? 2.2)]);
    const ex = { bid, ext: true };
    segment(true, y, x - T / 2, x + w + T / 2, gaps('n', x), ex);
    segment(true, y + h, x - T / 2, x + w + T / 2, gaps('s', x), ex);
    segment(false, x, y, y + h, gaps('w', y), ex);
    segment(false, x + w, y, y + h, gaps('e', y), ex);
    for (const d of doors) {
      const a = d.at, b = d.at + (d.width ?? 2.2);
      if (d.side === 'n' || d.side === 's') { const yy = d.side === 'n' ? y : y + h; lintels.push({ x0: x + a, y0: yy - T / 2, x1: x + b, y1: yy + T / 2, bid }); }
      else { const xx = d.side === 'w' ? x : x + w; lintels.push({ x0: xx - T / 2, y0: y + a, x1: xx + T / 2, y1: y + b, bid }); }
    }
    return bid;
  }
  // Porte verrouillée dans une ouverture (x0, y0)–(x1, y1) : un mur tant qu'elle n'est pas ouverte ; plusieurs portes
  // peuvent partager la même serrure (même clé)
  const lockDoor = (id: string, name: string, key: string, x0: number, y0: number, x1: number, y1: number) => {
    wall(x0, y0, x1, y1, { height: 2.4, look: 'door', door: id });
    if (!locks.some(l => l.id === id)) locks.push({ id, name, key, x: (x0 + x1) / 2, y: (y0 + y1) / 2 });
  };
  const partH = (bid: number, y: number, x0: number, x1: number, gapAt: number, gw = 1.6) => segment(true, y, x0, x1, [[x0 + gapAt, x0 + gapAt + gw]], { bid });
  const partV = (bid: number, x: number, y0: number, y1: number, gapAt: number, gw = 1.6) => segment(false, x, y0, y1, [[y0 + gapAt, y0 + gapAt + gw]], { bid });

  // ================================================================ Voirie

  floor(0, 3, W, 13, 'gravel'); // voie ferrée
  floor(0, 56, W, 63, 'asphalt'); // route départementale (est-ouest)
  floor(77, 13, 84, H, 'asphalt'); // rue de la Mairie, puis route de Meyrac vers le sud
  floor(86, 166, W, 172, 'asphalt'); // chemin des Combes, à travers le bois jusqu'au hameau
  floor(0, 54, 75, 56, 'sidewalk'); floor(86, 54, 160, 56, 'sidewalk');
  floor(0, 63, 75, 65, 'sidewalk'); floor(86, 63, 160, 65, 'sidewalk');
  floor(75, 13, 77, 56, 'sidewalk'); floor(84, 13, 86, 56, 'sidewalk');
  floor(75, 63, 77, 120, 'sidewalk'); floor(84, 63, 86, 120, 'sidewalk');
  floor(6, 33, 42, 51, 'parking'); // parking de la supérette
  floor(88, 34, 124, 52, 'concrete'); // place de la mairie
  floor(128, 35, 154, 50, 'parking'); // parking du commissariat
  floor(118, 12.5, 152, 15.5, 'concrete'); // quai de la gare
  // Marquage : tirets au milieu des routes, places de parking
  for (let x = 2; x < W; x += 6) if (x + 3 < 75 || x > 86) lines.push({ x0: x, y0: 59.42, x1: x + 3, y1: 59.58, w: 0.16 });
  for (let y = 15; y < H; y += 6) if (y + 3 < 54 || (y > 65 && (y + 3 < 166 || y > 172))) lines.push({ x0: 80.42, y0: y, x1: 80.58, y1: y + 3, w: 0.16 });
  for (let x = 90; x < W; x += 6) lines.push({ x0: x, y0: 168.92, x1: x + 3, y1: 169.08, w: 0.14 });
  for (let x = 8; x <= 40; x += 3) { lines.push({ x0: x, y0: 33.5, x1: x + 0.12, y1: 38.5, w: 0.12 }); lines.push({ x0: x, y0: 45.5, x1: x + 0.12, y1: 50.5, w: 0.12 }); }
  for (let x = 130; x <= 152; x += 3) lines.push({ x0: x, y0: 35.5, x1: x + 0.12, y1: 40.5, w: 0.12 });
  // Rails (décor, on les enjambe) : deux voies posées sur le ballast
  for (const ry of [6.6, 8.0, 9.6, 11.0]) lines.push({ x0: 0, y0: ry, x1: W, y1: ry + 0.12, w: 0.12, rail: true });
  // Grillage du périmètre de quarantaine
  fence(true, 0, 0, W); fence(true, H, 0, W); fence(false, 0, 0, H); fence(false, W, 0, H);

  // ================================================================ Saint-Aubry (le bourg)

  // Supérette (nord-ouest), réserve au fond
  let b = building('Supérette', 8, 16, 22, 14, [{ side: 's', at: 9, width: 3 }, { side: 'n', at: 16 }], 0xc9c2b2, 0x4a4a4e, 'tiles', { style: 'render', roofKind: 'flat' });
  partH(b, 20, 8, 30, 10, 2);
  for (const sx of [12, 16, 20, 24]) low(sx, 22.5, 1, 5.5, 1.6, 'shelf');
  low(9, 27.5, 2.5, 1, 1.1, 'counter');
  cont(10, 18.2, 'caisse'); cont(26, 18.2, 'caisse'); cont(28.8, 25, 'armoire', 3); cont(10.2, 26.8, 'bureau', 2);
  prop('box', 14, 18.5); prop('box', 15, 18.7, 1); prop('Pallet', 21, 18.2, 1);
  // Réserve fermée à clé (porte intérieure et porte de livraison)
  lockDoor('superette', 'Réserve de la supérette', 'k_superette', 18, 20 - T / 2, 20, 20 + T / 2);
  lockDoor('superette', 'Réserve de la supérette', 'k_superette', 24, 16 - T / 2, 26.2, 16 + T / 2);
  cont(17, 17.4, 'coffre');

  // Maison des Lilas (entre la gare et la route)
  b = building('Maison des Lilas', 46, 18, 12, 10, [{ side: 's', at: 4 }, { side: 'e', at: 2 }], 0xd8cdb4, 0x7a3f32, 'wood', { style: 'plaster' });
  partV(b, 52, 18, 28, 1.5); partH(b, 23, 46, 52, 2);
  cont(47.2, 19.4, 'armoire', 0); cont(56.8, 26.5, 'bureau', 3); cont(47, 26.8, 'medical', 2);
  prop('bed_frame', 55, 21, 3, 'low'); prop('sofa', 49, 25, 2, 'low'); prop('rugRectangle', 50, 25.5);

  // Pharmacie (sur la route)
  b = building('Pharmacie', 56, 38, 14, 14, [{ side: 's', at: 5, width: 3 }, { side: 'n', at: 9 }], 0xe1e4e0, 0x3c5a4a, 'tiles', { style: 'render', roofKind: 'flat' });
  partH(b, 46, 56, 70, 6, 2);
  low(58, 48.2, 4, 0.9, 1.1, 'counter');
  cont(57.1, 40.5, 'medical', 1); cont(68.9, 40.5, 'medical', 3); cont(62, 39.4, 'medical'); cont(66, 44.7, 'bureau', 2);
  for (const sx of [64, 67]) low(sx, 49, 0.9, 2.4, 1.6, 'shelf');
  // Réserve des médicaments, fermée à clé
  lockDoor('pharmacie', 'Réserve de la pharmacie', 'k_pharma', 62, 46 - T / 2, 64, 46 + T / 2);
  lockDoor('pharmacie', 'Réserve de la pharmacie', 'k_pharma', 65, 38 - T / 2, 67.2, 38 + T / 2);
  cont(59, 43.8, 'coffre', 1);

  // Mairie (au nord de la place)
  b = building('Mairie', 96, 16, 22, 14, [{ side: 's', at: 9.5, width: 3 }, { side: 'w', at: 6 }, { side: 'e', at: 6 }], 0xe9e2d0, 0x3a3f4a, 'wood', { style: 'render', roofKind: 'slate' });
  partV(b, 107, 16, 30, 5, 2); partH(b, 23, 96, 107, 5); partH(b, 23, 107, 118, 5);
  cont(98, 17.6, 'bureau'); cont(110, 17.6, 'bureau'); cont(116.7, 26, 'armoire', 3); cont(99, 28.4, 'bureau', 2);
  prop('bookcaseOpen', 104, 16.7); prop('bookcaseOpen', 113, 16.7); prop('table_wood', 101, 26, 0, 'low'); prop('chair', 101, 24.7, 2); prop('chair', 101, 27.3);

  // Commissariat (nord-est du bourg) : armurerie au fond
  b = building('Commissariat', 128, 18, 22, 14, [{ side: 'w', at: 7 }, { side: 's', at: 4 }], 0xb9c0c6, 0x2c3440, 'tiles', { style: 'painted', roofKind: 'flat' });
  partV(b, 139, 18, 32, 3, 2); partH(b, 25, 139, 150, 4);
  low(130, 23.5, 3, 0.9, 1.1, 'counter');
  cont(148.8, 20, 'casier', 3); cont(145.6, 19, 'casier'); cont(131, 19.4, 'bureau'); cont(148.5, 30.5, 'medical', 3); cont(141, 30.6, 'bureau', 2);

  // École (sud-ouest du bourg) : quatre salles et un couloir
  b = building('École Jean-Moulin', 30, 72, 28, 20, [{ side: 'n', at: 12, width: 3 }, { side: 'e', at: 8 }, { side: 's', at: 6 }], 0xd9c7a0, 0x6b3b2c, 'wood', { style: 'brick', roofKind: 'clay' });
  partH(b, 82, 30, 58, 6, 2); partH(b, 82, 30, 58, 20, 2); partV(b, 44, 72, 82, 4); partV(b, 44, 82, 92, 4);
  for (const [cx, cy] of [[37, 76], [51, 76], [37, 87], [51, 87]] as const) {
    for (const dx of [-3, 0, 3]) prop('table_wood', cx + dx, cy, 0, 'low');
    prop('desk_metal', cx, cy - 3, 2, 'low');
  }
  cont(31.4, 73.2, 'armoire', 1); cont(56.7, 73.5, 'bureau', 3); cont(31.2, 90.6, 'medical', 1); cont(56.6, 90.5, 'armoire', 3);

  // Maisons du lotissement (ouest)
  for (const [hx, hy, wc, rc] of [[6, 70, 0xd6c3a4, 0x5b3a2e], [6, 88, 0xc8cfc4, 0x3f4b55]] as const) {
    b = building('Maison', hx, hy, 12, 10, [{ side: 'n', at: 3 }, { side: 'e', at: 6 }], wc, rc, 'wood', { style: 'plaster' });
    partV(b, hx + 6, hy, hy + 10, 6); partH(b, hy + 5, hx + 6, hx + 12, 2);
    cont(hx + 1.2, hy + 8.6, 'armoire', 2); cont(hx + 10.8, hy + 1.6, 'bureau', 3); cont(hx + 10.8, hy + 8.5, 'medical', 3);
    prop('sofa', hx + 3, hy + 6.5, 2, 'low'); prop('bed_frame', hx + 9, hy + 2.5, 1, 'low'); prop('kitchenFridge', hx + 1.0, hy + 1.2, 1, 'low');
  }

  // Garage Moreau (sud de la route) : grande porte ouverte, une voiture bâchée en réparation
  b = building('Garage Moreau', 96, 70, 22, 16, [{ side: 'n', at: 4, width: 9 }, { side: 'e', at: 8 }], 0xb8b2a6, 0x384048, 'concrete', { style: 'painted', roofKind: 'metal' });
  partV(b, 110, 70, 86, 3, 2);
  prop('car', 103, 79, 0, 'low');
  cont(97.2, 84.6, 'outils', 2); cont(108.6, 84.6, 'caisse', 2); cont(116.8, 73, 'casier', 3); cont(113, 84.7, 'bureau', 2);
  for (const [px, py] of [[98, 72], [99, 72.4], [116, 84.5]] as const) prop('barrel', px, py, 0, 'low');
  prop('tyre', 106.5, 72.2, 0); prop('tyre', 107.2, 72.4, 0); prop('jerrycan', 100, 84.8); prop('generator', 113, 72.5, 0, 'low');

  // Maisons du quartier est
  for (const [hx, hy, wc, rc] of [[128, 70, 0xe0d4bc, 0x6e3a2f], [144, 70, 0xcfc8b8, 0x464b52], [128, 94, 0xd3c6ae, 0x5a3d33], [144, 94, 0xc5ccd0, 0x3c4650]] as const) {
    b = building('Maison', hx, hy, 12, 10, [{ side: 'n', at: 3 }, { side: 's', at: 8 }], wc, rc, 'wood', { style: (hx + hy) % 3 ? 'plaster' : 'painted' });
    partV(b, hx + 6, hy, hy + 10, 6); partH(b, hy + 5, hx, hx + 6, 3);
    cont(hx + 1.3, hy + 1.6, 'armoire', 1); cont(hx + 10.8, hy + 8.6, 'bureau', 3); cont(hx + 1.2, hy + 8.6, 'medical', 1);
    prop('bed_frame', hx + 9, hy + 3, 1, 'low'); prop('table_wood', hx + 3, hy + 6.5, 0, 'low');
  }

  // Wagons de marchandises abandonnés sur la voie : murs pleine hauteur, bons abris
  for (const wx of [24, 31, 62, 69, 104, 190, 197, 252]) { prop('Container_Green', wx, 8.3, 0); wall(wx - 3, 7, wx + 3, 9.6, { height: 2.7, look: 'model' }); }
  // Barrage militaire au carrefour
  for (const [bx, by] of [[78.2, 52.5], [80.5, 52.5], [82.8, 52.5], [78.2, 66.5], [82.8, 66.5]] as const) prop('jersey', bx, by, 0, 'low');
  for (const [cx, cy] of [[73.5, 57], [73.5, 62], [87.5, 57], [87.5, 62]] as const) prop('jersey', cx, cy, 1, 'low');
  // Voitures abandonnées (bâchées) : couverts au milieu des rues et des parkings
  for (const [cx, cy, rot] of [[14, 58, 1], [38, 61, 3], [58, 58.2, 1], [101, 60.5, 3], [124, 58, 1], [148, 61, 3], [80.5, 30, 0], [80.5, 92, 2], [80.5, 108, 0],
    [12, 36, 0], [24, 48, 2], [33, 36, 0], [136, 38, 0], [145, 48, 2], [176, 61, 3], [214, 58, 1], [262, 60.6, 3], [80.5, 140, 2], [80.5, 190, 0],
    [120, 168.6, 1], [196, 169.4, 3]] as const) prop('car', cx, cy, rot, 'low');
  // Place de la mairie : fontaine, bancs, arbres
  low(102, 41, 4, 4, 0.8, 'fountain');
  for (const [bx, by, rot] of [[96, 38, 1], [96, 48, 1], [112, 38, 3], [112, 48, 3]] as const) prop('bench', bx, by, rot);
  for (const [tx, ty] of [[90, 36], [122, 36], [90, 50], [122, 50], [104, 35.5]] as const) { prop('oak', tx, ty, 0); props[props.length - 1]!.free = (tx * 7 + ty) % 6.28; wall(tx - 0.3, ty - 0.3, tx + 0.3, ty + 0.3, { height: 3, look: 'model' }); }
  // Lampadaires le long de la route
  for (let x = 10; x < W; x += 18) { if (x > 70 && x < 90) continue; prop('lamp', x, 54.6, 1); prop('lamp', x + 9, 64.4, 3); }
  // Petit mobilier urbain et déchets
  for (const [px, py, m, rot] of [[31, 31.5, 'trashbag', 0], [32, 31.2, 'trashbag', 1], [55, 53.2, 'hydrant', 0], [126, 53.2, 'hydrant', 0], [72, 35, 'trash_can', 1],
    [93, 66, 'trashbag', 0], [119, 67, 'barrel', 0], [151, 34, 'barrel', 0], [40, 70, 'TrafficCone_1', 0], [41, 70.3, 'TrafficCone_1', 0],
    [43, 53.4, 'trash_can', 0], [88, 53.4, 'utility_box', 2], [74.4, 70, 'utility_box', 1], [33, 31.4, 'trash_can', 0]] as const) prop(m, px, py, rot);
  for (const [mx, my] of [[80.5, 40], [80.5, 75], [30, 59.5], [120, 59.5], [80.5, 150]] as const) prop('manhole', mx, my);
  low(36, 52, 3.2, 0.6, 1, 'barrier'); low(120, 32.5, 3.2, 0.6, 1, 'barrier'); low(62, 67, 3.2, 0.6, 1, 'barrier');
  // Caisses de ravitaillement abandonnées dehors
  cont(44, 40, 'caisse'); cont(150, 14, 'caisse'); cont(90, 98, 'caisse', 1); cont(20, 112, 'caisse'); cont(122, 112, 'caisse');

  // ================================================================ Zone industrielle des Fourches (nord-est)

  floor(168, 16, 244, 52, 'concrete');
  // Entrepôt Duval : grande halle, rayonnages, quai de chargement
  b = building('Entrepôt Duval', 172, 18, 34, 22, [{ side: 's', at: 6, width: 4 }, { side: 's', at: 22, width: 4 }, { side: 'e', at: 9 }, { side: 'n', at: 26 }], 0xb6b9b8, 0x5c6266, 'concrete', { style: 'metal', roofKind: 'metal' });
  for (const sx of [176, 181, 186, 191, 196]) low(sx, 22, 1.1, 9, 2.2, 'shelf');
  partV(b, 200, 18, 40, 13, 2.4);
  cont(203.6, 20, 'bureau', 0); cont(204.6, 30, 'armoire', 3); cont(174, 38.5, 'outils', 2); cont(188, 38.5, 'caisse', 2); cont(178.5, 34, 'caisse', 1);
  for (const [px, py] of [[183, 35], [184.4, 35], [195, 35.5]] as const) prop('Pallet', px, py, 1);
  prop('box', 183, 35.2); prop('box', 183.5, 34.6, 1); prop('generator', 198, 38.6, 0, 'low');
  // Coopérative agricole
  b = building('Coopérative', 214, 20, 24, 16, [{ side: 's', at: 4, width: 3 }, { side: 'w', at: 6 }], 0xb08a6c, 0x5a5048, 'concrete', { style: 'brick', roofKind: 'metal' });
  partH(b, 28, 226, 238, 4, 2);
  low(216, 22, 8, 1, 1.6, 'shelf'); low(216, 26, 8, 1, 1.6, 'shelf');
  cont(236.4, 22.6, 'bureau', 3); cont(229, 34.4, 'outils', 2); cont(236.5, 33, 'caisse', 3); cont(222, 34.4, 'caisse', 2);
  for (const [px, py] of [[218, 30.5], [219, 31.4], [220, 30.5]] as const) prop('barrel', px, py, 0, 'low');
  // Parc à conteneurs grillagé, poste de garde
  floor(246, 16, 294, 52, 'gravel');
  enclosure(246, 16, 294, 52, { s: [[262, 268]], w: [[30, 35]] });
  for (const [cx, cy, rot] of [[252, 22, 0], [252, 26, 0], [262, 22, 0], [272, 22, 1], [286, 22, 0], [286, 26, 0], [258, 36, 1], [270, 34, 0], [270, 44, 0]] as const) prop('Container_Green', cx, cy, rot, 'wall');
  b = building('Poste de garde', 281, 40, 9, 7, [{ side: 'w', at: 2.4 }], 0xc4c0b4, 0x50565c, 'concrete', { style: 'render', roofKind: 'flat' });
  cont(288.3, 42.5, 'casier', 3); cont(284, 45.7, 'bureau', 2);
  cont(263, 29, 'caisse'); cont(278, 30, 'outils'); cont(250, 46, 'caisse', 1);
  prop('generator', 278, 48.5, 0, 'low'); prop('jerrycan', 279.2, 48.6); prop('barrel', 256, 48.5, 0, 'low'); prop('barrel', 257, 49.2, 0, 'low');

  // ================================================================ Station-service et camp de quarantaine (est, sud de la route)

  floor(166, 65, 206, 90, 'concrete');
  b = building('Station-service', 170, 74, 14, 10, [{ side: 'n', at: 5, width: 2.4 }, { side: 'e', at: 6 }], 0xe4e2dc, 0x5a5c60, 'tiles', { style: 'render', roofKind: 'flat' });
  partV(b, 179, 74, 84, 6.5);
  for (const sx of [172, 175]) low(sx, 77.5, 0.9, 4, 1.6, 'shelf');
  low(180, 76, 0.9, 2.5, 1.1, 'counter');
  cont(182.6, 82.6, 'bureau', 3); cont(171.4, 82.6, 'caisse', 2); cont(177.6, 75.4, 'medical', 0);
  for (const [px, py] of [[190, 72], [196, 72], [190, 80], [196, 80]] as const) prop('utility_box', px, py, 0, 'low');
  prop('jerrycan', 191, 74.2); prop('jerrycan', 197.2, 81.6); prop('trash_can', 186, 72);

  floor(218, 74, 294, 118, 'gravel');
  enclosure(218, 74, 294, 118, { n: [[250, 256]], w: [[92, 98]] });
  const TENT = { style: 'render', roofKind: 'metal' } as const;
  b = building('Infirmerie du camp', 224, 80, 14, 8, [{ side: 's', at: 5, width: 2.4 }], 0x7a8166, 0x4c5440, 'wood', TENT);
  cont(225.5, 81.6, 'medical', 0); cont(236.5, 81.6, 'medical', 0); cont(231, 86.4, 'caisse', 2);
  prop('bed_frame', 228, 83.5, 1, 'low'); prop('bed_frame', 233, 83.5, 1, 'low');
  b = building('Poste de commandement', 246, 82, 12, 8, [{ side: 's', at: 4.5, width: 2.4 }], 0x6f7660, 0x4c5440, 'wood', TENT);
  cont(247.8, 83.6, 'bureau', 0); cont(256.6, 87.5, 'militaire', 3); prop('table_wood', 252, 84.6, 0, 'low');
  b = building('Dortoir', 266, 80, 20, 8, [{ side: 's', at: 8, width: 2.4 }], 0x7a8166, 0x4c5440, 'wood', TENT);
  for (const bx of [268, 271, 278, 281, 284]) prop('bed_frame', bx, 82, 0, 'low');
  cont(269, 86.6, 'armoire', 2); cont(283, 86.6, 'militaire', 2);
  b = building('Armurerie du camp', 270, 100, 14, 10, [{ side: 'w', at: 4, width: 2.4 }], 0x6f7660, 0x4c5440, 'concrete', { style: 'render', roofKind: 'flat' });
  cont(282.6, 102, 'casier', 3); cont(282.6, 107, 'casier', 3); cont(276, 108.6, 'militaire', 2); cont(273, 101.6, 'militaire', 0); cont(278, 101.4, 'coffre');
  lockDoor('camp_armory', 'Armurerie du camp', 'k_camp', 270 - T / 2, 104, 270 + T / 2, 106.4);
  for (const [cx, cy, rot] of [[230, 98, 0], [244, 104, 1], [258, 98, 0], [234, 110, 1]] as const) prop('jersey', cx, cy, rot, 'low');
  for (const [cx, cy] of [[240, 94], [252, 108], [262, 112]] as const) cont(cx, cy, 'militaire');
  prop('car', 228, 106, 0, 'low'); prop('car', 290, 92, 0, 'low');
  prop('generator', 262, 92, 0, 'low'); prop('barrel', 264, 92.4, 0, 'low'); prop('jerrycan', 263, 93.6);

  // ================================================================ Ferme des Combes (sud-ouest)

  floor(14, 130, 72, 162, 'dirt'); floor(72, 142, 77, 148, 'dirt');
  b = building('Ferme des Combes', 18, 134, 14, 11, [{ side: 'e', at: 4 }, { side: 's', at: 9 }], 0xd2c6ae, 0x7a4636, 'wood', { style: 'render', roofKind: 'clay' });
  partV(b, 25, 134, 145, 6); partH(b, 139, 18, 25, 3);
  cont(19.4, 135.6, 'armoire', 1); cont(30.6, 135.8, 'bureau', 3); cont(19.3, 143.4, 'medical', 1);
  prop('bed_frame', 22.5, 136.6, 1, 'low'); prop('table_wood', 29, 141.5, 0, 'low'); prop('kitchenFridge', 31, 143.6, 3, 'low');
  b = building('Grange', 40, 133, 24, 15, [{ side: 's', at: 8, width: 6 }, { side: 'w', at: 5 }], 0x8a7a66, 0x6a5a4c, 'concrete', { style: 'metal', roofKind: 'metal' });
  prop('car', 46, 139, 0, 'low');
  cont(61.8, 135.5, 'outils', 3); cont(58, 146.4, 'caisse', 2); cont(52.5, 134.6, 'caisse', 0);
  for (const [px, py] of [[42, 146], [43, 146.6], [62, 146]] as const) prop('barrel', px, py, 0, 'low');
  prop('generator', 54, 140, 1, 'low'); prop('tyre', 50, 146.6); prop('Pallet', 58.5, 140.5, 0);
  b = building('Hangar', 18, 151, 12, 8, [{ side: 'e', at: 2.5, width: 3 }], 0x6e5a48, 0x5c5248, 'dirt', { style: 'wood', roofKind: 'metal' });
  cont(19.6, 152.6, 'outils', 0); cont(19.6, 157.4, 'caisse', 2); prop('jerrycan', 28, 157.6);
  for (const [fx0, fx1] of [[12, 38], [42, 72]] as const) floor(fx0, 168, fx1, 206, 'dirt');
  for (let y = 170; y < 205; y += 2.2) for (const [fx0, fx1] of [[12, 38], [42, 72]] as const) lines.push({ x0: fx0 + 1, y0: y, x1: fx1 - 1, y1: y + 0.6, w: 0.6, furrow: true });
  fence(true, 128, 12, 72, [[34, 38]]);

  // ================================================================ Bois des Combes et cabanes de chasse

  b = building('Cabane de chasse', 140, 186, 8, 6, [{ side: 'n', at: 3 }], 0x6e5a48, 0x4a443e, 'wood', { style: 'wood', roofKind: 'slate' });
  cont(146.6, 190.5, 'casier', 3); cont(141.4, 190.6, 'armoire', 2);
  b = building('Cabane du garde', 112, 140, 7, 6, [{ side: 's', at: 2.4 }], 0x6e5a48, 0x4a443e, 'wood', { style: 'wood', roofKind: 'slate' });
  cont(113.3, 141.5, 'bureau', 1); cont(117.6, 141.4, 'caisse', 0);
  floor(142, 172, 145, 186, 'dirt'); floor(114, 146, 117, 166, 'dirt');

  // ================================================================ Hameau du Moulin (sud-est)

  floor(206, 160, 296, 166, 'sidewalk'); floor(206, 172, 296, 176, 'sidewalk');
  for (const [hx, hy, door, wc, rc] of [[210, 146, 's', 0xd8ccb2, 0x6e3a2f], [228, 146, 's', 0xcdc6b6, 0x3e464e], [214, 180, 'n', 0xd4c4a6, 0x7a4636], [234, 180, 'n', 0xc9cec8, 0x3c4650]] as const) {
    b = building('Maison', hx, hy, 12, 10, [{ side: door, at: 4 }, { side: door === 's' ? 'n' : 's', at: 8 }], wc, rc, 'wood', { style: (hx + hy) % 4 ? 'plaster' : 'render' });
    partV(b, hx + 6, hy, hy + 10, door === 's' ? 2 : 6);
    cont(hx + 1.2, hy + 1.5, 'armoire', 1); cont(hx + 10.8, hy + 8.5, 'bureau', 3); cont(hx + 10.8, hy + 1.5, 'medical', 3);
    prop('sofa', hx + 3, hy + 6.8, 0, 'low'); prop('bed_frame', hx + 8.6, hy + 5, 0, 'low');
  }
  b = building('Chapelle Saint-Roch', 250, 138, 12, 20, [{ side: 's', at: 4.8, width: 2.4 }, { side: 'e', at: 3 }], 0xcfc4b0, 0x3a3f46, 'tiles', { style: 'brick', roofKind: 'slate' });
  for (let y = 144; y < 156; y += 2.4) { prop('bench', 253, y, 0); prop('bench', 259, y, 0); }
  cont(256, 139.4, 'armoire', 0); cont(260.6, 140.5, 'medical', 3);
  b = building('Bar-tabac du Moulin', 270, 148, 16, 11, [{ side: 's', at: 6, width: 2.6 }], 0xdcc9a2, 0x5a3d33, 'wood', { style: 'painted', roofKind: 'clay' });
  partH(b, 153, 270, 286, 11);
  low(273, 155, 6, 0.9, 1.1, 'counter');
  cont(271.4, 149.6, 'caisse', 1); cont(284.6, 149.6, 'bureau', 3); cont(284.6, 157.4, 'armoire', 3);
  for (const tx of [280, 283]) prop('table_wood', tx, 156.5, 1, 'low');
  b = building('Moulin', 262, 182, 16, 14, [{ side: 'n', at: 5, width: 3 }, { side: 'w', at: 8 }], 0xb4a08a, 0x6b3b2c, 'wood', { style: 'brick', roofKind: 'clay' });
  partH(b, 189, 262, 278, 10, 2);
  cont(276.6, 184, 'outils', 3); cont(263.5, 194.4, 'caisse', 2); cont(276.6, 194.2, 'armoire', 3); prop('generator', 270, 192.5, 0, 'low');
  for (const [px, py, m] of [[224, 164, 'hydrant'], [249, 175.6, 'trash_can'], [268, 164.6, 'utility_box'], [208, 175.2, 'trashbag']] as const) prop(m, px, py);
  for (let x = 214; x < W; x += 20) { prop('lamp', x, 160.8, 1); prop('lamp', x + 10, 175.4, 3); }

  // ================================================================ Base militaire de Ravel (sud, après le bois)
  // Mur d'enceinte en béton, portail au nord, miradors aux angles. Garnison bien équipée, commandant au QG ;
  // bunker et salle des armes fermés à clé ; hélicoptère d'évacuation payant sur l'héliport.

  floor(200, 172, 207, 232, 'asphalt'); // route militaire depuis le chemin des Combes
  for (let y = 176; y < 230; y += 6) lines.push({ x0: 203.42, y0: y, x1: 203.58, y1: y + 3, w: 0.16 });
  floor(146, 232, 274, 292, 'concrete');
  const BASE_WALL: Partial<Rect> = { height: 3.4, look: 'basewall' };
  segment(true, 232, 146, 274, [[199, 208]], BASE_WALL);
  segment(true, 292, 146, 274, [], BASE_WALL);
  segment(false, 146, 232, 292, [], BASE_WALL);
  segment(false, 274, 232, 292, [], BASE_WALL);
  // Miradors (piliers de béton) et chicane du portail
  for (const [tx, ty] of [[148, 234], [272, 234], [148, 290], [272, 290]] as const) wall(tx - 1.2, ty - 1.2, tx + 1.2, ty + 1.2, { height: 6, look: 'tower' });
  for (const [jx, jy, rot] of [[197, 228.5, 0], [210, 228.5, 0], [201.5, 235.5, 0], [206.5, 237.6, 0]] as const) prop('jersey', jx, jy, rot, 'low');
  low(199.5, 231.2, 3, 0.5, 1.1, 'barrier'); // barrière levée à moitié : on passe à droite
  b = building('Poste de contrôle', 210, 234, 7, 6, [{ side: 'w', at: 2 }], 0x8a8f86, 0x4c5440, 'concrete', { style: 'render', roofKind: 'flat' });
  cont(215.8, 235.4, 'bureau', 3); cont(215.8, 238.6, 'casier', 3);

  // Héliport (ouest) : grand H peint, extraction payante
  floor(154, 240, 176, 262, 'parking');
  for (const [x0, y0, x1, y1] of [[160, 245, 161, 257], [169, 245, 170, 257], [161, 250.5, 169, 251.5]] as const) lines.push({ x0, y0, x1, y1, w: 1 });
  for (const [px, py] of [[155, 241], [175, 241], [155, 261], [175, 261]] as const) prop('TrafficCone_1', px, py);

  // QG : bureaux, salle radio, salle des armes fermée à clé
  b = building('QG de la base', 182, 240, 28, 18, [{ side: 's', at: 12, width: 3 }, { side: 'w', at: 6 }], 0x8f9488, 0x3c4238, 'tiles', { style: 'render', roofKind: 'flat' });
  partV(b, 196, 240, 258, 8, 1.6); partH(b, 248, 196, 210, 6, 1.6);
  lockDoor('base_armory', 'Salle des armes du QG', 'k_base', 202, 248 - T / 2, 203.6, 248 + T / 2);
  cont(208.6, 241.6, 'casier', 3); cont(208.6, 245.8, 'casier', 3); cont(198, 241.4, 'militaire', 0); cont(203, 241.4, 'coffre', 0);
  cont(183.6, 241.6, 'bureau', 1); cont(190, 241.4, 'bureau', 0); cont(183.6, 256.4, 'armoire', 1); cont(208.6, 256.4, 'militaire', 3); cont(200, 256.4, 'bureau', 2);
  prop('table_wood', 189, 249, 0, 'low'); prop('chair', 189, 247.7, 2); prop('chair', 189, 250.3, 0); prop('bookcaseClosed', 186, 240.7);

  // Hangars : véhicules, caisses militaires
  b = building('Hangar nord', 220, 238, 30, 18, [{ side: 's', at: 6, width: 6 }, { side: 'w', at: 8 }], 0x7d8276, 0x4a5048, 'concrete', { style: 'metal', roofKind: 'metal' });
  prop('Vehicle_Truck', 232, 246, 1, 'low'); prop('Vehicle_Pickup', 243, 247, 1, 'low');
  cont(221.6, 239.8, 'militaire', 0); cont(248.4, 239.8, 'militaire', 3); cont(248.4, 254.4, 'outils', 3); cont(222, 254.4, 'caisse', 2);
  for (const [px, py] of [[226, 240], [227, 240.6], [238, 240]] as const) prop('barrel', px, py, 0, 'low');
  b = building('Hangar sud', 214, 264, 26, 22, [{ side: 'n', at: 4, width: 6 }, { side: 'e', at: 12 }], 0x7d8276, 0x4a5048, 'concrete', { style: 'metal', roofKind: 'metal' });
  for (const [cx, cy, rot] of [[220, 276, 1], [230, 280, 0]] as const) prop('Container_Green', cx, cy, rot, 'wall');
  cont(236.4, 266, 'militaire', 3); cont(216, 284.4, 'militaire', 2); cont(236.4, 284.4, 'caisse', 3); cont(226, 266, 'outils', 0);
  prop('generator', 233, 270, 0, 'low'); prop('Pallet', 225, 271, 0);

  // Casernement : lits, armoires
  b = building('Casernement', 154, 268, 28, 14, [{ side: 'n', at: 12, width: 2.4 }, { side: 'e', at: 5 }], 0x8f9488, 0x3c4238, 'wood', { style: 'render', roofKind: 'metal' });
  partV(b, 168, 268, 282, 5);
  for (const bx of [156.5, 159.5, 162.5, 171, 174, 177]) prop('bed_frame', bx, 279.5, 0, 'low');
  cont(155.4, 269.6, 'armoire', 1); cont(166.6, 269.6, 'armoire', 3); cont(180.6, 269.6, 'armoire', 3); cont(166.5, 280.6, 'militaire', 2);

  // Bunker : porte blindée, le meilleur butin de la carte
  b = building('Bunker', 252, 264, 16, 14, [{ side: 'n', at: 6, width: 2.4 }], 0x6c706a, 0x4a4e48, 'concrete', { style: 'render', roofKind: 'flat' });
  lockDoor('bunker', 'Porte blindée du bunker', 'k_bunker', 258, 264 - T / 2, 260.4, 264 + T / 2);
  cont(253.6, 266, 'coffre', 1); cont(266.4, 266, 'coffre', 3); cont(253.6, 276.4, 'militaire', 1); cont(266.4, 276.4, 'militaire', 3); cont(260, 276.4, 'casier', 2);
  // Couverts dans la cour : sacs (jersey), caisses, citernes
  for (const [jx, jy, rot] of [[192, 264, 0], [196, 264, 0], [244, 262, 1], [186, 286, 0], [204, 288, 0], [252, 286, 0]] as const) prop('jersey', jx, jy, rot, 'low');
  for (const [px, py] of [[214, 260], [215, 260.8], [178, 236]] as const) prop('barrel', px, py, 0, 'low');
  cont(196, 270, 'militaire'); cont(250, 258, 'militaire', 1); cont(180, 262, 'caisse');
  for (let x = 152; x < 274; x += 20) prop('lamp', x, 233.2, 0);

  // ================================================================ Végétation

  // Arbres, haies, troncs morts et souches dans les zones vertes, loin des rues et des bâtiments
  const blocked = (x: number, y: number, pad: number) =>
    floors.some(f => (f.mat !== 'gravel' || f.y0 > 14) && x > f.x0 - pad && x < f.x1 + pad && y > f.y0 - pad && y < f.y1 + pad) || rects.some(R => x > R.x0 - pad && x < R.x1 + pad && y > R.y0 - pad && y < R.y1 + pad);
  // Bois : surtout des résineux ; bourg et abords : des feuillus et quelques sapins
  const FOREST = ['fir_a', 'fir_b', 'fir_c', 'fir_a', 'pine_a', 'pine_b', 'young_fir_a', 'young_fir_b', 'oak'];
  const OPEN = ['oak', 'oak', 'oak', 'fir_b', 'fir_c', 'pine_a', 'young_fir_a'];
  const shrubs = ['shrub_a', 'shrub_b', 'shrub_c', 'shrub_d'];
  const scatter = (x0: number, y0: number, x1: number, y1: number, count: number, treeShare: number, trees = OPEN) => {
    for (let k = 0, placed = 0; k < count * 14 && placed < count; k++) {
      const x = x0 + r() * (x1 - x0), y = y0 + r() * (y1 - y0);
      const roll = r();
      if (roll < treeShare) {
        if (blocked(x, y, 2.5)) continue;
        props.push({ model: trees[Math.floor(r() * trees.length)]!, x, y, rot: 0, free: r() * 6.28 });
        wall(x - 0.3, y - 0.3, x + 0.3, y + 0.3, { height: 3, look: 'model' });
      } else if (roll < treeShare + 0.12) {
        if (blocked(x, y, 3)) continue;
        // Tronc couché : un couvert bas, dans une direction quelconque mais sur les axes (collision rectangulaire)
        const rot = Math.floor(r() * 4);
        prop(r() < 0.5 ? 'log_big' : 'stump', x, y, rot, 'low');
      } else {
        if (blocked(x, y, 1.5)) continue;
        props.push({ model: shrubs[Math.floor(r() * shrubs.length)]!, x, y, rot: 0, free: r() * 6.28 });
      }
      placed++;
    }
  };
  scatter(88, 124, 204, 214, 260, 0.62, FOREST); // bois des Combes : dense
  scatter(3, 14, W - 3, 124, 130, 0.5); // bourg, zone industrielle et camp : arbres épars
  scatter(3, 124, 88, H - 3, 60, 0.45); // abords de la ferme
  scatter(204, 124, W - 3, 222, 70, 0.5); // abords du hameau
  scatter(3, 210, 144, H - 3, 90, 0.55, FOREST); // forêt de Ravel, à l'ouest de la base
  scatter(276, 210, W - 3, H - 3, 25, 0.5, FOREST);
  scatter(146, 214, 274, 230, 30, 0.5); // abords de l'enceinte

  const extracts: Extract[] = [
    { name: 'Quai de la gare', x: 155, y: 9, r: 3 },
    { name: 'Route de Meyrac', x: 80.5, y: 296, r: 3 },
    { name: 'Hélicoptère d’évacuation', x: 165, y: 251, r: 4, cost: 12000 },
    { name: 'Lisière ouest', x: 3.5, y: 59.5, r: 3 },
    { name: 'Pont de Vauclerc', x: 296, y: 59.5, r: 3 },
    { name: 'Sentier du Moulin', x: 296, y: 169, r: 3 },
  ];
  const spawns: [number, number][] = [[4, 40], [4, 108], [4, 150], [6, 212], [6, 280], [40, 296], [120, 296], [292, 286], [292, 230], [296, 130],
    [296, 100], [296, 30], [228, 6], [160, 6], [96, 6], [44, 5]];
  return { name: 'Saint-Aubry', width: W, height: H, rects, zones, spawns, containers, extracts, floors, buildings, props, lines, lintels, locks };
}

export const MAP: GameMap = buildMap();
